import { useState } from "react";
import { Tag, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/kit";

export const NONE = "No campaign";
export const campaignsOf = (coupons) => [...new Set((coupons || []).map((c) => c.campaign).filter(Boolean))].sort();

function totals(coupons) {
  const rows = {};
  (coupons || []).forEach((c) => {
    const k = c.campaign || NONE;
    const s = c.stats || {};
    const r = (rows[k] ||= { name: k, codes: 0, scans: 0, opens: 0, signups: 0 });
    r.codes += 1;
    r.scans += s.qr_scans || 0;
    r.opens += s.link_opens || 0;
    r.signups += s.signups || 0;
  });
  return Object.values(rows).sort((a, b) => b.signups - a.signups || b.scans + b.opens - a.scans - a.opens);
}

export function CampaignCompare({ coupons, active, onPick }) {
  const rows = totals(coupons);
  if (!rows.some((r) => r.name !== NONE)) return null;
  const best = rows[0]?.signups ? rows[0].name : null;
  return (
    <div data-testid="campaign-compare" className="mb-6 overflow-x-auto rounded-2xl border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2.5">Campaign</th><th className="px-4 py-2.5">Codes</th><th className="px-4 py-2.5">QR scans</th><th className="px-4 py-2.5">Link opens</th><th className="px-4 py-2.5">Sign-ups</th><th className="px-4 py-2.5">Conversion</th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const visits = r.scans + r.opens;
            return (
              <tr key={r.name} data-testid={`campaign-row-${r.name}`} onClick={() => onPick(active === r.name ? null : r.name)} className={`cursor-pointer border-t border-slate-100 hover:bg-emerald-50/50 ${active === r.name ? "bg-emerald-50" : ""}`}>
                <td className="px-4 py-2.5 font-semibold text-slate-900"><span className="inline-flex items-center gap-1.5">{r.name === best && <Trophy className="h-3.5 w-3.5 text-amber-500" />}{r.name}</span></td>
                <td className="px-4 py-2.5 font-mono">{r.codes}</td>
                <td className="px-4 py-2.5 font-mono">{r.scans}</td>
                <td className="px-4 py-2.5 font-mono">{r.opens}</td>
                <td className="px-4 py-2.5 font-mono font-bold text-slate-900">{r.signups}</td>
                <td className="px-4 py-2.5 font-mono">{visits ? `${Math.round((r.signups / visits) * 100)}%` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">Click a campaign to show only its codes. Conversion = sign-ups ÷ (QR scans + link opens); sign-ups include people who typed the code.</p>
    </div>
  );
}

export function CampaignInput({ value, onChange, options, testid }) {
  return (
    <>
      <input data-testid={testid} list="campaign-options" maxLength={60} className="ka-input" placeholder="e.g. Food Expo 2026" value={value} onChange={(e) => onChange(e.target.value)} />
      <datalist id="campaign-options">{options.map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
}

export function CampaignLabel({ c, options, onSaved }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(c.campaign || "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/platform/coupons/${c.id}`, { campaign: v });
      toast.success(v.trim() ? `${c.code} added to ${v.trim()}` : `Campaign removed from ${c.code}`);
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <>
      <button data-testid={`coupon-campaign-${c.code}`} onClick={() => { setV(c.campaign || ""); setOpen(true); }} className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${c.campaign ? "bg-sky-50 text-sky-800 ring-1 ring-sky-200" : "text-slate-400 hover:text-slate-700"}`}><Tag className="h-3 w-3" />{c.campaign || "Add campaign"}</button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle>Campaign for {c.code}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid gap-4">
              <Field label="Event or campaign" hint="Leave empty to remove the label."><CampaignInput testid="campaign-edit-input" value={v} onChange={setV} options={options} /></Field>
              <button data-testid="campaign-edit-save" className="ka-press rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">Save</button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
